import { gql } from '@apollo/client/core'
import {
  bsConstants,
  CreateUserStatusDto,
  EntityDeletionSummaryDto,
  IdArgs,
  IdDto,
  ReportedUserGetArgs,
  ReportedUserModelDto,
  UpdateUserDto,
  UpdateUserPreferencesDto,
  UpdateUserStatusDto,
  UserDto,
  UserReviewerStatsDto,
  UserStatsDto,
  UserStatusDto,
  UserStatusGetArgs
} from 'booksprout'
import { useApollo } from '../apollo/apolloModule'
import { useAxios } from '../axios/axiosModule'
import { useBaseCrud } from '../../composables/useBaseCrud'
import { makeUserResult } from './makeUserResult'
import { arcReviewClaimFragments } from '../arcs/fragments'
import authStoreGetters from '../authentication/store/getters'

/**
 * Replaces `UserService`.
 *
 * The class extended `BaseCrudService`, which pulled in `ConfigService`, `StoreService` and
 * `AxiosService` and forced three `throw new Error('Unauthorised')` stubs it never wanted. Its four
 * real helpers now come from `useBaseCrud`; the transports come from useApollo/useAxios.
 *
 * It also injected `ArcService` — 2,613 lines — for a single GraphQL fragment string, now imported
 * from `modules/arcs/fragments`.
 */

export const fragments = {
  userStatus: gql`
    fragment UserStatusFragment on UserStatusDto {
      id,
      active,
      additionalInformation,
      adminUser {
        id,
        emailAddress,
        name
      },
      adminUserId,
      createdDate,
      expiryDate,
      isRestriction,
      type,
      userId
      ineligibleReason
    }
  `,
  userProfile: gql`
    fragment UserProfileFragment on UserDto {
      id,
      createdDate,
      reviewerRole,
      authorRole,
      emailAddress,
      name,
      amazonProfileLink,
      consentMethod,
      kindleEmail,
      verifiedKindleEmail,
      image,
      termsConsentRevokedDate,
      topReviewerAppStatus,
      topReviewerAppStatusDate,
      topReviewerAppStatusReason,
      stripeCustomerId,
      isAdmin,
      isLocked,
      forceLoyaltyDiscountView,
      canActAs {
        id,
        emailAddress
      },
      userEmails {
        id,
        emailAddress,
        isDefault,
        status,
        postmarkBounceCode,
        postmarkBounceName,
        postmarkBounceDescription,
        postmarkBounceDate,
        postmarkBounceCount
      },
      preferences {
        alertNewArc,
        alertNewArcOtherLanguages,
        alertNewArcDigest,
        newArcDigestFrequency,
        arcDigestBannerDismissedAt,
        alertReviewDueIn2Days,
        dailyUpdatesOnFollowCount,
        penNameLimit,
        reminderBookLinks,
        reviewSitesFlag,
        reviewLanguagesFlag,
        alertTimeToPublish,
        alertReadyToReview,
        alertFinalFollowup,
        alertDelayExpiring,
        alertDelayExpired,
        alertSavedArcEndingOrLowCopies,
        alertNewArcParticipant
        alertNewReview,
        alertArcCancellation,
        alertReviewRejected,
        alertNewExternalReview,
        alertNewTeamApplicant,
        emailOnMessage,
        invitedToTeam,
        freeText,
        findArcFilters,
        uiLocale,
        alertBookFileUpdated
      },
      status,
      termsConsentDate,
      following {
        id
      },
      hasActingAs,
      completedTours,
      countryCode,
      usedCouponCodes,
      phoneNumber,
      phoneVerified,
      legacyBestsellingArcs,
      maxRunningArcsOverride
    }
  `
}

const crud = useBaseCrud<UserDto>('user')

/**
 * Strip out preferences which we know the server won't update i.e penNameLimit
 * If we don't do this, an error will show saying it can't update that value
 * This is for safety. We don't want users adding props manually and updating their prefs.
 * @param preferences
 */
const sanitisePreferences = (preferences: UpdateUserPreferencesDto | undefined): UpdateUserPreferencesDto | undefined => {
  if (preferences === void 0) return void 0

  const {
    // @ts-expect-error — neither key is on the DTO; omitting them is the whole point.
    penNameLimit: _penNameLimit,
    // @ts-expect-error — nor is the apollo cache marker.
    __typename: _typename,
    ...result
  } = preferences

  return result
}

const getUsersToMimic = (needle: string): Promise<UserDto[]> => {
  return useApollo().query({
    variables: {
      needle
    },
    query: gql`
      query GetUsersToMimic ($needle: String!) {
        getUsersToMimic (needle: $needle) {
          id,
          emailAddress,
          name,
          authorRole,
          reviewerRole,
          image
        }
      }
    `
  }).then((r: any) => {
    return r.map(makeUserResult)
  })
}

/**
 * Shouldn't be used. Auth service will handle
 * @param model
 */
const create = (_model: UserDto): Promise<UserDto> => {
  throw new Error('Unauthorised')
}

/**
 * Will only work for admin users (API limitation)
 * @param id
 */
const checkDelete = (id: number): Promise<EntityDeletionSummaryDto[]> => {
  return useApollo().query({
    variables: {
      id
    },
    query: gql`
      query CheckDeleteUser ($id: Int!) {
        checkDeleteUser (id: $id) {
          count,
          dtoTypeName,
          ids
        }
      }
    `
  })
}

/**
 * Will only work for admin users (API limitation)
 * @param id
 */
const deleteUser = (id?: number): Promise<any> => {
  return useApollo().mutate({
    variables: {
      id
    },
    mutation: gql`
      mutation ConfirmDeleteUser (
        $id: Int!
      ) {
        confirmDeleteUser(id: $id) {
          id
        }
      }
    `
  })
}

const checkDeleteMyAccount = (): Promise<EntityDeletionSummaryDto[]> => {
  return useApollo().query({
    query: gql`
      query CheckDeleteMyAccount {
        checkDeleteMyAccount {
          count,
          dtoTypeName,
          ids
        }
      }
    `
  })
}

const deleteMyAccount = (confirmation: string): Promise<IdDto> => {
  return useApollo().mutate({
    variables: {
      confirmation
    },
    mutation: gql`
      mutation ConfirmDeleteMyAccount (
        $confirmation: String!
      ) {
        confirmDeleteMyAccount(confirmation: $confirmation) {
          id
        }
      }
    `
  })
}

/**
 * Shouldn't be used. Auth service will handle
 * @param model
 */
const get = (_params?: any): Promise<UserDto[]> => {
  throw new Error('Unauthorised')
}

/**
 * Shouldn't be used. Auth service will handle
 * @param model
 */
const getById = (id: number, args?: any): Promise<UserDto | null> => {
  if (args?.mimicUser) {
    return useApollo().query({
      query: gql`
        query GetMimicUserProfile {
          getMimicUserProfile {
            ...UserProfileFragment
          }
        }
        ${fragments.userProfile}
      `
    }).then((r: any) => {
      return makeUserResult(r)
    })
  } else {
    return useApollo().query({
      query: gql`
        query GetUserProfile {
          getUserProfile {
            ...UserProfileFragment
          }
        }
        ${fragments.userProfile}
      `
    }).then((r: any) => {
      return makeUserResult(r)
    })
  }
}

/**
 * Profile updates ONLY. Won't work for password changes.
 * @param model
 */
const update = (model: UpdateUserDto): Promise<UserDto> => {
  return useApollo().mutate({
    variables: {
      id: model.id,
      amazonProfileLink: model.amazonProfileLink,
      image: model.image,
      kindleEmail: model.kindleEmail,
      verifiedKindleEmail: model.verifiedKindleEmail,
      name: model.name,
      preferences: sanitisePreferences(model.preferences),
      notificationRegistrationToken: model.notificationRegistrationToken,
      completedTours: model.completedTours,
      authorRole: model.authorRole,
      skipThirdPartySync: model.skipThirdPartySync
    },
    mutation: gql`
      mutation UpdateUserProfile (
        $id: Int!,
        $amazonProfileLink: String,
        $image: String,
        $kindleEmail: String,
        $verifiedKindleEmail: Boolean,
        $name: String,
        $preferences: UpdateUserPreferencesDto,
        $notificationRegistrationToken: String,
        $completedTours: String
        $authorRole: String
        $skipThirdPartySync: Boolean
      ) {
        updateUserProfile (
          id: $id,
          amazonProfileLink: $amazonProfileLink,
          image: $image,
          kindleEmail: $kindleEmail,
          verifiedKindleEmail: $verifiedKindleEmail,
          name: $name,
          preferences: $preferences,
          notificationRegistrationToken: $notificationRegistrationToken,
          completedTours: $completedTours
          authorRole: $authorRole
          skipThirdPartySync: $skipThirdPartySync
        ) {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  }).then((r: any) => {
    return makeUserResult(r)
  })
}

const addNewEmailAddress = (emailAddress: string): Promise<UserDto> => {
  return useApollo().mutate({
    variables: {
      emailAddress
    },
    mutation: gql`
      mutation AddNewEmailAddress (
        $emailAddress: String!
      ) {
        addNewEmailAddress (emailAddress: $emailAddress) {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  }).then((r: any) => {
    return makeUserResult(r)
  })
}

const removeEmailAddress = (emailAddress: string): Promise<UserDto> => {
  return useApollo().mutate({
    variables: {
      emailAddress
    },
    mutation: gql`
      mutation RemoveEmailAddress (
        $emailAddress: String!
      ) {
        removeEmailAddress (emailAddress: $emailAddress) {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  }).then((r: any) => {
    return makeUserResult(r)
  })
}

const makeDefaultEmailAddress = (emailAddress: string): Promise<UserDto> => {
  return useApollo().mutate({
    variables: {
      emailAddress
    },
    mutation: gql`
      mutation SetDefaultEmailAddress (
        $emailAddress: String!
      ) {
        setDefaultEmailAddress (emailAddress: $emailAddress) {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  }).then((r: any) => {
    return makeUserResult(r)
  })
}

const withdrawContent = (): Promise<any> => {
  return useAxios().axios().post('user/withdraw-consent')
}

const reinstateContent = (): Promise<any> => {
  return useAxios().axios().post('user/reinstate-consent')
}

const consentGiven = (): Promise<any> => {
  return useAxios().axios().post('user/consent-given')
}

const addVirtualAssistant = (emailAddress: string): Promise<UserDto> => {
  return useApollo().mutate({
    variables: {
      emailAddress
    },
    mutation: gql`
      mutation AddVirtualAssistant (
        $emailAddress: String!
      ) {
        addVirtualAssistant (emailAddress: $emailAddress) {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  }).then((r: any) => {
    return makeUserResult(r)
  })
}

const removeVirtualAssistant = (emailAddress: string): Promise<any> => {
  return useApollo().mutate({
    variables: {
      emailAddress
    },
    mutation: gql`
      mutation RemoveVirtualAssistant (
        $emailAddress: String!
      ) {
        removeVirtualAssistant (emailAddress: $emailAddress) {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  })
}

const addAuthorRoleToUser = (): Promise<UserDto> => {
  return useApollo().mutate({
    mutation: gql`
      mutation AddAuthorRoleToUser {
        addAuthorRoleToUser {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  })
}

const getUserStats = (): Promise<UserStatsDto> => {
  return useApollo().query({
    query: gql`
      query GetUserStats {
        getUserStats {
          completeArcCount,
          newReviewCount,
          pendingTeamMembers,
          arcCount,
          bookCount,
          penNameCount,
          arcTeamCount,
          reviewsCount,
          anyTeamHasMember,
          anyArcHasParticipant,
          anyArcHasParticipant,
          anyPenNameHasFollower,
          hasReviewOlderThanOneMonth,
          hasHadTeamMemberForOneMonth
        }
      }
    `
  })
}

const getReviewerStats = (args: IdArgs): Promise<UserReviewerStatsDto> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetReviewerStats (
        $id: Int!
      ) {
        getReviewerStats (
          id: $id
        ) {
          arcsDownloadedThisWeek,
          earliestNextClaimDate,
          arcTotalOverdueDays,
          onTimeReviewRateStats {
            allClaimsCount,
            lateCount,
            onTimeReviewRate,
            pcOfLateClaims
          },
          isTrustedReviewer,
          totalReviewCount,
          averageRating,
          cancelledReviewCount,
          dueReviewCount,
          activeArcCount
        }
      }
    `
  })
}

const applyForTopReviewer = (): Promise<UserDto> => {
  return useApollo().mutate({
    mutation: gql`
      mutation ApplyForTopReviewerRole {
        applyForTopReviewerRole {
          ...UserProfileFragment
        }
      }
      ${fragments.userProfile}
    `
  })
}

const addUserStatus = (args: CreateUserStatusDto): Promise<UserStatusDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation AddUserStatus (
        $userId: Int!,
        $type: Int!,
        $active: Boolean!,
        $isRestriction: Boolean!,
        $additionalInformation: String,
        $expiryDate: DateTime
      ) {
        addUserStatus (
          userId: $userId,
          type: $type,
          active: $active,
          isRestriction: $isRestriction,
          expiryDate: $expiryDate,
          additionalInformation: $additionalInformation
        ) {
          ...UserStatusFragment
        }
      }
      ${fragments.userStatus}
    `
  })
}

const updateUserStatus = (args: UpdateUserStatusDto): Promise<UserStatusDto> => {
  return useApollo().mutate({
    variables: {
      ...args
    },
    mutation: gql`
      mutation UpdateUserStatus (
        $id: Int!,
        $type: Int,
        $active: Boolean,
        $isRestriction: Boolean,
        $additionalInformation: String,
        $expiryDate: DateTime
        $userId: Int!
        $ineligibleReason: Int
      ) {
        updateUserStatus (
          id: $id,
          type: $type,
          active: $active,
          isRestriction: $isRestriction,
          expiryDate: $expiryDate,
          additionalInformation: $additionalInformation,
          userId: $userId,
          ineligibleReason: $ineligibleReason,
        ) {
          ...UserStatusFragment
        }
      }
      ${fragments.userStatus}
    `
  })
}

const getUserStatuses = (args: UserStatusGetArgs): Promise<UserStatusDto[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetUserStatuses (
        $active: Boolean,
        $type: Int,
        $adminUserId: Int,
        $needle: String,
        $isRestriction: Boolean,
        $userId: Int!
      ) {
        getUserStatuses (
          userId: $userId,
          active: $active,
          type: $type,
          adminUserId: $adminUserId,
          needle: $needle,
          isRestriction: $isRestriction
        ) {
          ...UserStatusFragment
        }
      }
      ${fragments.userStatus}
    `
  })
}

/**
 * Returns whether the user has an active Amazon review restriction. Uses the user's
 * preferences from the store when available, otherwise fetches via getUserStatuses.
 */
const getHasAmazonRestriction = async (userId: number): Promise<boolean> => {
  /**
   * Reads the authentication store directly. This used to go through
   * `storeService.store.state.auth`, which stopped being written the moment auth state moved out
   * of Vuex — so the cached-preference path was silently dead and every call refetched.
   */
  const mimic = authStoreGetters.mimicUser
  const authenticated = authStoreGetters.authenticatedUser
  const user = mimic?.id === userId
    ? mimic
    : authenticated?.id === userId
      ? authenticated
      : null
  const stored = user?.preferences?.hasAmazonReviewRestriction
  if (stored !== undefined && stored !== null) {
    return stored
  }
  const restrictions = await getUserStatuses({
    userId,
    isRestriction: true,
    active: true
  })
  return restrictions.some(
    r => r.type === bsConstants.USER.USER_STATUS.PREVENT_AMAZON_REVIEWS && r.active
  )
}

const getUserReports = (args: ReportedUserGetArgs): Promise<ReportedUserModelDto[]> => {
  return useApollo().query({
    variables: {
      ...args
    },
    query: gql`
      query GetUserReports (
        $userId: Int!,
        $sinceLastRestriction: Boolean,
        $ignored: Boolean,
        $system: Boolean
      ) {
        getUserReports (
          userId: $userId,
          sinceLastRestriction: $sinceLastRestriction,
          ignored: $ignored,
          system: $system
        ) {
          date,
          extendedReason,
          reasonType,
          reportedByUser {
            id,
            name,
            emailAddress,
            image
          },
          reportedDocumentId,
          type,
          ignored,
          claim {
            ...ArcReviewClaimFragment
          }
        }
      },
      ${arcReviewClaimFragments.reviewClaim}
    `
  })
}

const addLeadToCampaign = (ref: string): Promise<boolean> => {
  if (ref) {
    return useAxios().axios().post('first-promoter/add-lead', {
      ref
    })
  }

  return Promise.resolve(false)
}

const reactivateEmailAddresses = (): Promise<void> => {
  return useAxios().axios().post('email/reactivate-email-addresses')
}

/**
 * `globalLoader` is chained (`globalLoader(false).getUsersToMimic(...)`), so it has to hand back
 * this bag rather than the apollo module's. Declared against `service` so the reference resolves
 * at call time.
 */
const service = {
  ...crud,

  fragments,

  // crud
  get,
  getById,
  create,
  update,
  delete: deleteUser,
  checkDelete,
  checkDeleteMyAccount,
  deleteMyAccount,

  // profile
  makeUserResult,
  getUsersToMimic,
  getUserStats,
  getReviewerStats,
  applyForTopReviewer,
  addAuthorRoleToUser,
  getHasAmazonRestriction,

  // email addresses
  addNewEmailAddress,
  removeEmailAddress,
  makeDefaultEmailAddress,
  reactivateEmailAddresses,

  // consent + content
  withdrawContent,
  reinstateContent,
  consentGiven,

  // virtual assistants
  addVirtualAssistant,
  removeVirtualAssistant,

  // statuses + reports
  addUserStatus,
  updateUserStatus,
  getUserStatuses,
  getUserReports,

  addLeadToCampaign,

  globalLoader (enable: boolean) {
    crud.setGlobalLoader(enable)
    return service
  }
}

export function userService () {
  return service
}
